import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Button,
  FormControlLabel,
  Chip,
} from '@mui/material';
import customerService from '../services/customerService';
import type { Customer, CustomerFilters } from '../models/Customer';
import { toE164Morocco } from '../services/banService';

interface CustomerPhoneSelectorProps {
  selectedPhones: string[];
  onChange: (phones: string[]) => void;
}

/**
 * Sélecteur de clients par téléphone, pour restreindre un code promo.
 *
 * - Recherche par nom / téléphone / email
 * - Filtre "clients actifs uniquement"
 * - Pagination simple ("Charger plus")
 * - Retourne une liste de téléphones normalisés en E.164 (+212...)
 */
const CustomerPhoneSelector: React.FC<CustomerPhoneSelectorProps> = ({ selectedPhones, onChange }) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingMore, setFetchingMore] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showActiveOnly, setShowActiveOnly] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<any | undefined>(undefined);

  const loadCustomers = async (append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setFetchingMore(true);
      }

      const trimmed = searchTerm.trim();
      const digits = trimmed.replace(/[^\d]/g, '');
      const isPhoneLike = !!trimmed && /^[+\d][\d+\s]*$/.test(trimmed) && digits.length >= 3;
      const isEmailLike = !!trimmed && trimmed.includes('@');

      const baseFilters: CustomerFilters = {
        isActive: showActiveOnly ? true : undefined,
      };

      // Cas 1 : téléphone ou email -> on laisse le backend filtrer (comme avant)
      if (isPhoneLike || isEmailLike) {
        const res = await customerService.listCustomers(
          { ...baseFilters, searchTerm: trimmed },
          50,
          append ? nextCursor : undefined
        );
        setCustomers(prev => (append ? [...prev, ...res.customers] : res.customers));
        setNextCursor(res.nextCursor);
        return;
      }

      // Cas 2 : aucun terme de recherche -> ne pas charger toute la base, juste une page
      if (!trimmed) {
        const res = await customerService.listCustomers(
          baseFilters,
          50,
          append ? nextCursor : undefined
        );
        setCustomers(prev => (append ? [...prev, ...res.customers] : res.customers));
        setNextCursor(res.nextCursor);
        return;
      }

      // Cas 3 : recherche par nom/texte -> on veut :
      // - insensible à la casse
      // - seulement les noms qui COMMENCENT par le terme (Ali, Alia...) pas Bakali
      // - sur TOUTE la base
      let all: Customer[] = [];
      let cursorLocal: any | undefined = undefined;

      for (;;) {
        const res = await customerService.listCustomers(
          baseFilters,
          200,
          cursorLocal
        );
        all = all.concat(res.customers);
        if (!res.nextCursor) {
          break;
        }
        cursorLocal = res.nextCursor;
      }

      const term = trimmed.toLowerCase();

      const filtered = all.filter((c) => {
        const first = (c.firstName || '').toLowerCase();
        const last = (c.lastName || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        // Noms : préfixe seulement (Ali -> Ali, Alia, ALI..., pas Bakali)
        const nameMatch = first.startsWith(term) || last.startsWith(term);
        // Téléphone/email : on peut garder contains pour aider la recherche
        const contactMatch = phone.includes(term) || email.startsWith(term);
        return nameMatch || contactMatch;
      });

      setCustomers(filtered);
      setNextCursor(undefined); // on a tout chargé, pas de pagination "Charger plus" dans ce mode
    } catch (e) {
      console.error('Erreur chargement clients pour sélecteur promo:', e);
    } finally {
      setLoading(false);
      setFetchingMore(false);
    }
  };

  useEffect(() => {
    // Chargement initial
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadCustomers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Recharger quand recherche / filtre actif change
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    loadCustomers(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, showActiveOnly]);

  const togglePhone = (rawPhone: string | undefined | null) => {
    const normalized = toE164Morocco(rawPhone || undefined);
    if (!normalized) return;
    const current = new Set(selectedPhones);
    if (current.has(normalized)) {
      current.delete(normalized);
    } else {
      current.add(normalized);
    }
    onChange(Array.from(current));
  };

  const isSelected = (rawPhone: string | undefined | null): boolean => {
    const normalized = toE164Morocco(rawPhone || undefined);
    if (!normalized) return false;
    return selectedPhones.includes(normalized);
  };

  const selectedCount = selectedPhones.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle2">Clients sélectionnés</Typography>
        <Chip
          size="small"
          color={selectedCount > 0 ? 'primary' : 'default'}
          label={selectedCount > 0 ? `${selectedCount} client(s)` : 'Tous les clients'}
        />
      </Box>

      <TextField
        size="small"
        fullWidth
        placeholder="Rechercher un client (nom, téléphone, email)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            size="small"
          />
        }
        label="Clients actifs uniquement"
      />

      <Box
        sx={{
          flex: 1,
          minHeight: 200,
          maxHeight: 360,
          overflow: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <List dense>
            {customers.map((c) => {
              const fullName = `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Client';
              const phoneDisplay = c.phone || 'N/A';
              const emailDisplay = c.email || '';
              const disabled = !toE164Morocco(c.phone);
              return (
                <ListItem
                  key={c.id}
                  disablePadding
                >
                  <ListItemButton
                    onClick={() => !disabled && togglePhone(c.phone)}
                    disabled={disabled}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected(c.phone)}
                        tabIndex={-1}
                        disableRipple
                        disabled={disabled}
                        inputProps={{ 'aria-labelledby': c.id }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${fullName} — ${phoneDisplay}`}
                      secondary={emailDisplay}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
            {customers.length === 0 && !loading && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                Aucun client trouvé pour cette recherche.
              </Typography>
            )}
          </List>
        )}
      </Box>

      {nextCursor && !loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => loadCustomers(true)}
            disabled={fetchingMore}
          >
            {fetchingMore ? 'Chargement...' : 'Charger plus'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default CustomerPhoneSelector;
